import {
  AddressesApi,
  BaseAPI,
  CategoriesApi,
  CmsApi,
  CmsFormSubmissionApi,
  Configuration,
  DeliverySlotsApi,
  MegacardApi,
  OrdersApi,
  ProductsApi,
  ProductSearchApi,
  ShoppingCartsApi,
  ShoppingListsApi,
  ShopsApi,
  UsersApi,
} from '@/api';
import Axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import KeycloakService from '@/services/KeycloakService';
import { Config } from '@/util/Config';
import { ErrorStore } from '@/stores/error.store';
import ErrorKey from '@/util/ErrorKey';
import { getModule } from 'vuex-module-decorators';
import { store } from '@/stores/index';
import { StandardError, StandardErrors } from '@/util/StandardErrors';
import UserStore from '@/stores/user/user.store';

export * from './response';

class ApiProxyHandler<A extends BaseAPI> {

  public static async handleRequest(args: AxiosRequestConfig): Promise<AxiosRequestConfig> {
    const userStore = getModule(UserStore, store);
    const token = KeycloakService.accessToken;
    if (token) {
      args.withCredentials = true;
      args.headers.common.Authorization = `Bearer ${token}`;
      args.headers.common['Accept-Language'] = userStore.currentLanguage;
      await KeycloakService.initOnce();
      return ApiProxyHandler.refreshToken(args);
    } else {
      return args;
    }
  }

  public static handleResponseError(error: AxiosError): Promise<never> {
    const errorStore = getModule(ErrorStore, store);
    let standardError: StandardError;
    if (error.response) {
      standardError = StandardErrors.wrapResponse(error.response);
    } else {
      standardError = {errorKey: ErrorKey.NoServerResponse, parameters: []};
    }
    errorStore.setError(standardError);
    return Promise.reject(standardError);
  }

  public static handleResponseSuccess(response: any): any {
    return response;
  }

  private static async refreshToken(args: AxiosRequestConfig) {
    try {
      const tokenChanged = await KeycloakService.updateToken(15);
      if (tokenChanged) {
        const token = KeycloakService.updateLocalStorageToken();
        args.headers.Authorization = `Bearer ${token}`;
      }
      return args;
    } catch (e) {
      KeycloakService.logout();
      throw e;
    }
  }
}

const gmAxios = Axios.create();
gmAxios.interceptors.request.use(ApiProxyHandler.handleRequest);
gmAxios.interceptors.response.use(ApiProxyHandler.handleResponseSuccess, ApiProxyHandler.handleResponseError);

const basePath = Config.HUB_API_BASE;

const wrap =
  <A extends BaseAPI>(TargetType: new (config?: Configuration, basePath?: string, axios?: AxiosInstance) => A): A => {
    return new TargetType({}, basePath, gmAxios);
  };

export const usersApi = wrap(UsersApi);
export const categoriesApi = wrap(CategoriesApi);
export const productSearchApi = wrap(ProductSearchApi);
export const productsApi = wrap(ProductsApi);
export const shoppingCartsApi = wrap(ShoppingCartsApi);
export const addressesApi = wrap(AddressesApi);
export const deliverySlotsApi = wrap(DeliverySlotsApi);
export const ordersApi = wrap(OrdersApi);
export const shopsApi = wrap(ShopsApi);
export const megacardApi = wrap(MegacardApi);
export const cmsApi = wrap(CmsApi);
export const cmsFormSubmissionApi = wrap(CmsFormSubmissionApi);
export const shoppingListsApi = wrap(ShoppingListsApi);
